import { useEffect, useState, type ReactNode } from "react";
import { ExternalLink, Loader2, MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  buildTelegramConnectUrl,
  isTelegramLinked,
  useVipGroupInfo,
} from "@/lib/firebase";

const TOKEN_WAIT_MS = 20_000;

type Props = {
  userId: string | undefined;
};

function ConnectSteps({ action }: { action?: ReactNode }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The button below opens Telegram and connects you to the official Real Pranks Super
        Sub group chat.
      </p>
      <ol className="space-y-2 text-sm leading-relaxed">
        <li>
          <span className="font-bold text-foreground">1.</span> Make sure you&apos;re signed
          into the Telegram account you want to use.
        </li>
        <li>
          <span className="font-bold text-foreground">2.</span> Tap{" "}
          <strong>Connect Telegram</strong> below.
        </li>
        <li>
          <span className="font-bold text-foreground">3.</span> Join the group and start
          chatting with the community.
        </li>
      </ol>
      {action}
    </div>
  );
}

export function TelegramConnectGuide({ userId }: Props) {
  const { info, loading } = useVipGroupInfo(userId, Boolean(userId));
  const linked = isTelegramLinked(info);
  const token = info?.telegramLinkToken?.token;
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);

  useEffect(() => {
    if (loading || linked || token || !userId) {
      setWaitedLongEnough(false);
      return;
    }
    const timer = window.setTimeout(() => setWaitedLongEnough(true), TOKEN_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [linked, loading, token, userId]);

  if (!userId) {
    return (
      <ConnectSteps
        action={
          <p className="text-sm text-muted-foreground">
            Sign in to get your personal Telegram invite.
          </p>
        }
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Preparing your Telegram invite…
      </div>
    );
  }

  if (linked) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Connected
          {info?.telegramUsername ? ` as @${info.telegramUsername}` : ""}. Messages stay
          synced between the website, Telegram, and the team Slack channel.
        </p>
        <Button asChild className="gap-2">
          <a href="https://t.me/rc_vip_user_bot" target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4" />
            Open in Telegram
          </a>
        </Button>
      </div>
    );
  }

  return (
    <ConnectSteps
      action={
        <>
          {token ? (
            <Button asChild className="w-full gap-2 sm:w-auto">
              <a href={buildTelegramConnectUrl(token)} target="_blank" rel="noreferrer">
                <MessageCircle className="h-4 w-4" />
                Connect Telegram
              </a>
            </Button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {waitedLongEnough
                ? "Still preparing your invite. You can also use the Join VIP Telegram button in your welcome email."
                : "Preparing your personal Telegram invite…"}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            This invite link is connected to your account. Please don&apos;t share it.
          </p>
        </>
      }
    />
  );
}
